if (typeof ProductLifestyleGallery === 'undefined') {
  // Shared URL converter used by both the inline carousel iframes and the modal.
  // Converts a plain share URL to an embed URL with autoplay, muted, loop,
  // no platform controls.
  // muted=true  → video starts muted  (carousel inline embeds)
  // muted=false → video starts unmuted (modal popup)
  function plgConvertToEmbedUrl(url, muted = true) {
    url = url.trim();

    // YouTube: youtube.com/watch?v=ID  or  youtu.be/ID
    const ytMatch = url.match(
      /(?:youtube\.com\/watch\?(?:.*&)?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    if (ytMatch) {
      const id = ytMatch[1];
      return (
        `https://www.youtube.com/embed/${id}` +
        `?autoplay=1&mute=${muted ? 1 : 0}&loop=1&playlist=${id}&controls=0&enablejsapi=1`
      );
    }

    // Vimeo: vimeo.com/ID
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return (
        `https://player.vimeo.com/video/${vimeoMatch[1]}` +
        `?autoplay=1&muted=${muted ? 1 : 0}&loop=1&controls=0&api=1`
      );
    }

    // Streamable: streamable.com/CODE  (not already /e/)
    const streamableMatch = url.match(/streamable\.com\/(?!e\/)([a-zA-Z0-9]+)/);
    if (streamableMatch) {
      return (
        `https://streamable.com/e/${streamableMatch[1]}` +
        `?autoplay=1&muted=${muted ? 1 : 0}&loop=1&nocontrols=1&logo=false`
      );
    }

    return url;
  }

  class PLGVideoModal {
    constructor(sectionContainer) {
      this.section = sectionContainer;
      this.modal = sectionContainer.querySelector('.plg-modal-overlay');
      if (!this.modal) return;

      this.progressFill   = this.modal.querySelector('.plg-progress-fill');
      this.muteBtn        = this.modal.querySelector('.plg-mute-btn');
      this.closeBtns      = this.modal.querySelectorAll('.plg-close-btn');
      this.prevBtn        = this.modal.querySelector('.plg-nav-prev');
      this.nextBtn        = this.modal.querySelector('.plg-nav-next');
      this.modalContainer = this.modal.querySelector('.plg-modal-container');
      this.videoContainer = this.modal.querySelector('.plg-video-container');

      this.mediaItems      = [];   // unified list: { type, url, slide }
      this.modalVideo      = null; // current <video> element (native Shopify)
      this.currentIframe   = null; // current <iframe> element (embed)
      this.currentIndex    = 0;
      this.isTransitioning = false;
      this.progressInterval = null;
      this.wheelTimeout    = null;
      this.isMuted         = false;
      this.header          = document.querySelector('.header-section');

      // Move modal to body so position:fixed is relative to viewport,
      // not a transformed ancestor (shopify-section-animation etc.)
      document.body.appendChild(this.modal);

      this._buildMediaList();
      this._bindSlideClicks();
      this._bindModalEvents();
      this._bindKeyboard();
      this._bindDesktopScroll();
      this._bindTouchSwipe();
    }

    // ── Media list ─────────────────────────────────────────────────────────
    // Builds a unified ordered list of all clickable slides (both native
    // Shopify videos and embed URL slides), preserving DOM order.
    _buildMediaList() {
      this.mediaItems = [];
      const allSlides = Array.from(
        this.section.querySelectorAll('.product-lifestyle-gallery__slide')
      );
      allSlides.forEach(slide => {
        if (slide.classList.contains('plg-has-embed') && slide.dataset.embedUrl) {
          this.mediaItems.push({ type: 'embed', url: slide.dataset.embedUrl, slide });
        } else if (slide.classList.contains('plg-has-video') && slide.dataset.videoUrl) {
          this.mediaItems.push({ type: 'video', url: slide.dataset.videoUrl, slide });
        }
      });

      // Keep legacy aliases so any external code referencing these still works
      this.videoSlides = this.mediaItems.map(i => i.slide);
      this.videoUrls   = this.mediaItems.map(i => i.url);
    }

    _bindSlideClicks() {
      // Respect the section-level "enable popup" toggle from the customizer.
      // data-enable-popup is "true" by default; only skip when explicitly "false".
      const enablePopup = this.section.dataset.enablePopup !== 'false';
      this.mediaItems.forEach((item, index) => {
        item.slide.style.cursor = enablePopup ? 'pointer' : 'default';
        if (enablePopup) {
          item.slide.addEventListener('click', () => this.open(index));
        }
      });
    }

    // ── URL converter ───────────────────────────────────────────────────────
    _convertToEmbedUrl(url) {
      return plgConvertToEmbedUrl(url);
    }

    // ── Element factories ───────────────────────────────────────────────────
    _createVideoElement(url) {
      const video = document.createElement('video');
      video.className = 'plg-modal-video';
      video.setAttribute('playsinline', '');
      video.setAttribute('preload', 'auto');
      video.src = url;
      video.muted = false;

      this.modalVideo    = video;
      this.currentIframe = null;
      this.isMuted       = false;
      this.modal.classList.remove('plg-embed-active');

      this.videoContainer.appendChild(video);
      this._bindVideoEvents();
      this._setMuteIcon(false);
      return video;
    }

    _createIframeElement(url) {
      const iframe = document.createElement('iframe');
      iframe.className = 'plg-modal-iframe';
      // Modal embeds always start unmuted (muted=false)
      iframe.src = plgConvertToEmbedUrl(url, false);
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('allow', 'autoplay; fullscreen; encrypted-media');

      this.currentIframe = iframe;
      this.modalVideo    = null;
      this.isMuted       = false; // modal embed starts unmuted
      this.modal.classList.add('plg-embed-active');

      this.videoContainer.appendChild(iframe);
      this._setMuteIcon(false);
      return iframe;
    }

    // Removes all media elements from the video container and resets state.
    _clearContainer() {
      this.videoContainer.querySelectorAll('video').forEach(v => {
        v.pause();
        v.src = '';
        v.load(); // flush audio pipeline across all browsers
        v.remove();
      });
      this.videoContainer.querySelectorAll('iframe').forEach(f => {
        f.src = '';
        f.remove();
      });
      this.modalVideo    = null;
      this.currentIframe = null;
      this._clearProgress();
    }

    // ── Open / Close ────────────────────────────────────────────────────────
    open(index) {
      if (this.mediaItems.length === 0) return;
      this.currentIndex = index;
      const item = this.mediaItems[index];

      this._clearContainer();

      this.modal.style.display = 'flex';
      // Force reflow then activate for CSS transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.modal.classList.add('active');
        });
      });

      document.body.style.overflow = 'hidden';
      document.body.classList.add('pdp-video-active');
      if (this.header) this.header.style.position = 'unset';

      if (item.type === 'video') {
        this._createVideoElement(item.url);
      } else {
        this._createIframeElement(item.url);
      }

      this._updateNavVisibility();
    }

    close() {
      this._clearContainer();
      this.modal.classList.remove('active', 'plg-embed-active');
      setTimeout(() => {
        this.modal.style.display = 'none';
      }, 300);

      document.body.style.overflow = '';
      document.body.classList.remove('pdp-video-active');
      this.isTransitioning = false;
      if (this.header) this.header.style.position = 'sticky';
    }

    // ── Navigation ──────────────────────────────────────────────────────────
    navigate(direction) {
      if (this.isTransitioning || this.mediaItems.length <= 1) return;
      this.isTransitioning = true;

      const total    = this.mediaItems.length;
      const newIndex = direction === 'next'
        ? (this.currentIndex + 1) % total
        : (this.currentIndex - 1 + total) % total;
      const newItem  = this.mediaItems[newIndex];

      // Build incoming element (starts off-screen)
      let incomingEl;
      if (newItem.type === 'video') {
        incomingEl = document.createElement('video');
        incomingEl.className = 'plg-modal-video';
        incomingEl.setAttribute('playsinline', '');
        incomingEl.setAttribute('preload', 'auto');
        incomingEl.src   = newItem.url;
        incomingEl.muted = false;
      } else {
        incomingEl = document.createElement('iframe');
        incomingEl.className = 'plg-modal-iframe';
        // Modal embeds always unmuted
        incomingEl.src = plgConvertToEmbedUrl(newItem.url, false);
        incomingEl.setAttribute('frameborder', '0');
        incomingEl.setAttribute('allowfullscreen', '');
        incomingEl.setAttribute('allow', 'autoplay; fullscreen; encrypted-media');
      }

      incomingEl.classList.add(
        direction === 'next' ? 'slide-in-from-down' : 'slide-in-from-up'
      );
      this.videoContainer.appendChild(incomingEl);

      const outgoingEl = this.currentIframe || this.modalVideo;

      const animate = () => {
        setTimeout(() => {
          if (outgoingEl) {
            outgoingEl.classList.add(
              direction === 'next' ? 'slide-out-up' : 'slide-out-down'
            );
          }
          incomingEl.classList.remove('slide-in-from-down', 'slide-in-from-up');
          incomingEl.classList.add('slide-in-center');
        }, 50);

        setTimeout(() => {
          // Remove outgoing element
          if (outgoingEl) {
            if (outgoingEl.tagName === 'VIDEO') {
              outgoingEl.pause();
              outgoingEl.src = '';
              outgoingEl.load();
            } else {
              outgoingEl.src = '';
            }
            outgoingEl.remove();
          }

          // Update state for incoming type
          if (newItem.type === 'video') {
            this.modalVideo    = incomingEl;
            this.currentIframe = null;
            this.isMuted       = false;
            this.modal.classList.remove('plg-embed-active');
            this._bindVideoEvents();
            this._setMuteIcon(false);
            this._startProgress();
          } else {
            this.currentIframe = incomingEl;
            this.modalVideo    = null;
            this.isMuted       = false; // modal embed always starts unmuted
            this.modal.classList.add('plg-embed-active');
            this._clearProgress();
            this._setMuteIcon(false);
          }

          this.currentIndex    = newIndex;
          this.isTransitioning = false;
          this._updateNavVisibility();
        }, 420);
      };

      if (newItem.type === 'video') {
        // Wait for enough data before animating (mirrors original logic)
        if (incomingEl.readyState >= 3) {
          incomingEl.play().catch(() => {}).finally(animate);
        } else {
          const go = () => {
            incomingEl.removeEventListener('canplay', go);
            incomingEl.removeEventListener('error',   go);
            incomingEl.play().catch(() => {}).finally(animate);
          };
          incomingEl.addEventListener('canplay', go, { once: true });
          incomingEl.addEventListener('error',   go, { once: true });
          setTimeout(() => { if (this.isTransitioning) go(); }, 2000);
        }
      } else {
        // iframes don't expose load events cross-origin — animate immediately
        animate();
      }
    }

    // ── Progress bar ────────────────────────────────────────────────────────
    _startProgress() {
      this._clearProgress();
      this.progressInterval = setInterval(() => {
        if (!this.modalVideo || !this.modalVideo.duration) return;
        const pct = (this.modalVideo.currentTime / this.modalVideo.duration) * 100;
        this.progressFill.style.width = pct + '%';
      }, 100);
    }

    _clearProgress() {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
      this.progressFill.style.width = '0%';
    }

    // ── Mute ────────────────────────────────────────────────────────────────
    _toggleMute() {
      this.isMuted = !this.isMuted;
      if (this.modalVideo) {
        // Native Shopify video — direct DOM property
        this.modalVideo.muted = this.isMuted;
      } else if (this.currentIframe) {
        // Embed — postMessage to the platform player
        this._postMessageMute(this.isMuted);
      }
      this._setMuteIcon(this.isMuted);
    }

    _postMessageMute(mute) {
      if (!this.currentIframe) return;
      const src = this.currentIframe.src || '';
      try {
        if (src.includes('youtube.com')) {
          // YouTube IFrame API (requires enablejsapi=1 in URL)
          this.currentIframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: mute ? 'mute' : 'unMute', args: [] }),
            '*'
          );
        } else if (src.includes('vimeo.com') || src.includes('streamable.com')) {
          // Vimeo Player API — Streamable uses the same postMessage format
          this.currentIframe.contentWindow.postMessage(
            JSON.stringify({ method: 'setVolume', value: mute ? 0 : 1 }),
            '*'
          );
        }
      } catch (e) {
        // Cross-origin postMessage failures are silently ignored
      }
    }

    _setMuteIcon(muted) {
      const svg = this.muteBtn.querySelector('svg');
      if (!svg) return;
      svg.style.opacity = muted ? '0.5' : '1';
    }

    // ── Play / Pause (native video only) ────────────────────────────────────
    _togglePlayPause() {
      if (!this.modalVideo) return;
      this.modalVideo.paused ? this.modalVideo.play() : this.modalVideo.pause();
    }

    // ── Nav visibility ──────────────────────────────────────────────────────
    _updateNavVisibility() {
      const show = this.mediaItems.length > 1;
      this.prevBtn.style.display = show ? 'flex' : 'none';
      this.nextBtn.style.display = show ? 'flex' : 'none';
    }

    // ── Video event binding ─────────────────────────────────────────────────
    _onVideoLoaded() {
      if (!this.modalVideo) return;
      this.modalVideo.muted = false;
      this.isMuted = false;
      this._setMuteIcon(false);
      this.modalVideo.play().catch(console.error);
      this._startProgress();
    }

    _bindVideoEvents() {
      if (!this.modalVideo) return;
      this.modalVideo.addEventListener('loadeddata', () => this._onVideoLoaded());
      this.modalVideo.addEventListener('ended',      () => setTimeout(() => this.navigate('next'), 400));
      this.modalVideo.addEventListener('click',      (e) => { e.stopPropagation(); this._togglePlayPause(); });
    }

    _bindModalEvents() {
      // Progress bar click — bound once, checks for active video
      const progressBar = this.modal.querySelector('.plg-progress-bar');
      progressBar.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.modalVideo && this.modalVideo.duration) {
          const rect = progressBar.getBoundingClientRect();
          this.modalVideo.currentTime =
            ((e.clientX - rect.left) / rect.width) * this.modalVideo.duration;
        }
      });

      // Close buttons
      this.closeBtns.forEach(btn => btn.addEventListener('click', (e) => {
        e.stopPropagation(); this.close();
      }));

      // Mute
      this.muteBtn.addEventListener('click', (e) => { e.stopPropagation(); this._toggleMute(); });

      // Prev / Next
      this.prevBtn.addEventListener('click', (e) => { e.stopPropagation(); this.navigate('prev'); });
      this.nextBtn.addEventListener('click', (e) => { e.stopPropagation(); this.navigate('next'); });

      // Click backdrop to close (desktop only)
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal && window.innerWidth >= 768) this.close();
      });

      // Prevent clicks inside the video container from bubbling to backdrop
      this.videoContainer.addEventListener('click', (e) => e.stopPropagation());
    }

    _bindKeyboard() {
      this._onKeydown = (e) => {
        if (!this.modal.classList.contains('active')) return;
        if (e.key === 'Escape')    this.close();
        if (e.key === 'ArrowUp')   { e.preventDefault(); this.navigate('prev'); }
        if (e.key === 'ArrowDown') { e.preventDefault(); this.navigate('next'); }
        if (e.key === ' ')         { e.preventDefault(); this._togglePlayPause(); }
        if (e.key === 'm' || e.key === 'M') this._toggleMute();
      };
      document.addEventListener('keydown', this._onKeydown);
    }

    _bindDesktopScroll() {
      let accumulated = 0;
      this.modalContainer.addEventListener('wheel', (e) => {
        e.preventDefault();
        accumulated += e.deltaY;
        clearTimeout(this.wheelTimeout);
        this.wheelTimeout = setTimeout(() => {
          if (Math.abs(accumulated) > 30) this.navigate(accumulated > 0 ? 'next' : 'prev');
          accumulated = 0;
        }, 150);
      }, { passive: false });

      // Mouse drag vertical
      let dragging = false, startY = 0;
      this.modalContainer.addEventListener('mousedown', (e) => {
        if (e.target.closest('.plg-video-controls, .plg-nav, .plg-progress-bar')) return;
        dragging = true; startY = e.clientY;
        this.modalContainer.style.cursor = 'grab';
        e.preventDefault();
      });
      this._onMouseup = (e) => {
        if (!dragging) return;
        const delta = startY - e.clientY;
        if (Math.abs(delta) > 30) this.navigate(delta > 0 ? 'next' : 'prev');
        dragging = false;
        this.modalContainer.style.cursor = '';
      };
      document.addEventListener('mouseup', this._onMouseup);
    }

    _bindTouchSwipe() {
      let startY = 0, startX = 0, startTime = 0;
      this.modalContainer.addEventListener('touchstart', (e) => {
        startY    = e.touches[0].clientY;
        startX    = e.touches[0].clientX;
        startTime = Date.now();
      }, { passive: true });

      this.modalContainer.addEventListener('touchend', (e) => {
        const dy = startY - e.changedTouches[0].clientY;
        const dx = Math.abs(startX - e.changedTouches[0].clientX);
        const dt = Date.now() - startTime;
        if (Math.abs(dy) > 40 && dt < 350 && Math.abs(dy) > dx) {
          this.navigate(dy > 0 ? 'next' : 'prev');
        }
      }, { passive: true });
    }

    destroy() {
      if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
      if (this._onMouseup) document.removeEventListener('mouseup', this._onMouseup);
      this.close();
      if (this.modal && this.modal.parentNode) this.modal.parentNode.removeChild(this.modal);
    }
  }

  // ─── Flickity carousel controller (unchanged) ─────────────────────────────
  class ProductLifestyleGallery {
    constructor(container) {
      this.container      = container;
      this.slidesContainer = container.querySelector('.product-lifestyle-gallery__slides');
      this.slidesWrapper   = container.querySelector('.product-lifestyle-gallery__slides-wrapper');
      this.flickityInstance = null;
      this.resizeTimer      = null;

      if (!this.slidesContainer || !this.slidesWrapper) return;

      // Init video modal if any video or embed slides exist
      if (container.querySelector('.plg-has-video, .plg-has-embed')) {
        this.videoModal = new PLGVideoModal(container);
      }

      this.init();
    }

    init() {
      this._initInlineEmbeds();
      this.checkFlickityInit();
      this.attachEventListeners();
    }

    // Injects autoplay muted looping iframes into every embed slide in the
    // carousel. Runs once on page load; the iframes keep playing independently
    // of whether the modal is open or closed.
    _initInlineEmbeds() {
      this.container.querySelectorAll('.plg-has-embed[data-embed-url]').forEach(slide => {
        const mediaEl = slide.querySelector('.product-lifestyle-gallery__media');
        if (!mediaEl || mediaEl.querySelector('.product-lifestyle-gallery__embed')) return;

        const iframe = document.createElement('iframe');
        iframe.className = 'product-lifestyle-gallery__embed';
        iframe.src = plgConvertToEmbedUrl(slide.dataset.embedUrl);
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('allow', 'autoplay; fullscreen; encrypted-media');
        mediaEl.appendChild(iframe);
      });
    }

    initFlickity() {
      if (window.Flickity && !this.flickityInstance) {
        const config = JSON.parse(this.slidesContainer.getAttribute('data-flickity-config'));
        const isLandingLayout = this.container.dataset.landingPageLayout === 'true';
        if (isLandingLayout && window.innerWidth >= 600) {
          config.cellAlign = 'left';
          config.contain = true;
        }
        this.flickityInstance = new Flickity(this.slidesContainer, config);
      }
    }

    destroyFlickity() {
      if (this.flickityInstance) {
        this.flickityInstance.destroy();
        this.flickityInstance = null;
      }
    }

    checkFlickityInit() {
      const windowWidth = window.innerWidth;
      const isLandingLayout = this.container.dataset.landingPageLayout === 'true';

      // When landing layout is active, destroy and re-init Flickity when
      // crossing the 600px breakpoint so cellAlign updates correctly.
      if (isLandingLayout) {
        const isMobile = windowWidth < 600;
        if (this._lastLandingMobile !== undefined && this._lastLandingMobile !== isMobile) {
          this.destroyFlickity();
        }
        this._lastLandingMobile = isMobile;
      }

      // Landing page layout on desktop: initialize Flickity only if slides > 5
      if (isLandingLayout && windowWidth >= 990) {
        const slideCount = this.slidesContainer.querySelectorAll(
          '.product-lifestyle-gallery__slide'
        ).length;
        if (slideCount > 5) {
          if (!this.flickityInstance) this.initFlickity();
        } else {
          this.destroyFlickity();
        }
        return;
      }

      // Below 1300px: always use Flickity
      if (windowWidth < 1300) {
        if (!this.flickityInstance) this.initFlickity();
        return;
      }

      // 1300px and above: count-based detection — scrollWidth is unreliable
      // with overflow-x: visible so we compare slide count against the natural
      // fit capacity of each size variant instead.
      const slideCount = this.slidesContainer.querySelectorAll(
        '.product-lifestyle-gallery__slide'
      ).length;

      let fitCount = 4; // normal: 4 slides fill 100% (4 × 25%)
      if (this.slidesContainer.classList.contains('small'))       fitCount = 5;
      if (this.slidesContainer.classList.contains('extra-small')) fitCount = 6;

      if (slideCount > fitCount) {
        if (!this.flickityInstance) this.initFlickity();
      } else {
        this.destroyFlickity();
      }
    }

    handleResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.checkFlickityInit(), 250);
    }

    attachEventListeners() {
      window.addEventListener('resize', this.handleResize.bind(this));
    }

    destroy() {
      this.destroyFlickity();
      window.removeEventListener('resize', this.handleResize.bind(this));
      if (this.videoModal) this.videoModal.destroy();
    }
  }

  // ─── Init helpers ──────────────────────────────────────────────────────────
  function initProductLifestyleGalleries() {
    document.querySelectorAll('.product-lifestyle-gallery').forEach(gallery => {
      if (!gallery.hasAttribute('data-gallery-initialized')) {
        new ProductLifestyleGallery(gallery);
        gallery.setAttribute('data-gallery-initialized', 'true');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductLifestyleGalleries);
  } else {
    initProductLifestyleGalleries();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const gallery = event.target.querySelector('.product-lifestyle-gallery');
    if (gallery && !gallery.hasAttribute('data-gallery-initialized')) {
      setTimeout(() => {
        new ProductLifestyleGallery(gallery);
        gallery.setAttribute('data-gallery-initialized', 'true');
      }, 100);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const gallery = event.target.querySelector('.product-lifestyle-gallery');
    if (gallery && gallery.galleryInstance) {
      gallery.galleryInstance.destroy();
      gallery.removeAttribute('data-gallery-initialized');
    }
  });
}
